import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { OperatorPageShell, Button } from '@/components';
import { useAuthFetch } from '@/hooks';
import { useAuthUser } from '@/auth.ts';
import { formatDateTimeFull } from '@/utils';
import { ROUTES } from '../../routes.ts';
import { PAGE_SIZE, TABLE_BTN } from './operatorTableConstants.ts';
import { Th, OperatorPagination, AccountStatusPill, Pill, type PillTone } from './operatorTableHelpers.tsx';
import panel from './operatorPanel.module.css';
import type { InviteDoctorResponse, OperatorDoctorListResponse, OperatorDoctorSummary, PendingDoctorInviteListResponse } from '@dermalink/shared';

type FilterType = '' | 'pending' | 'locked' | 'verified';

function doctorStatus(d: OperatorDoctorSummary): 'locked' | 'verified' | 'pending' {
  if (!d.identity_verified && d.verification_attempts >= 3) return 'locked';
  if (d.identity_verified && d.profession_verified && d.tos_accepted) return 'verified';
  return 'pending';
}

export function OperatorDoctorsPanel() {
  const authFetch = useAuthFetch();
  const [data, setData]         = useState<OperatorDoctorListResponse | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(false);
  const [page, setPage]         = useState(1);
  const [filter, setFilter]     = useState<FilterType>('');
  const [inviteEmail, setInviteEmail]   = useState('');
  const [inviteRiziv, setInviteRiziv]   = useState('');
  const [inviteQualification, setInviteQualification] = useState('');
  const [inviteState, setInviteState]   = useState<'idle' | 'loading' | 'success' | 'conflict' | 'error'>('idle');
  const [inviteResult, setInviteResult] = useState<InviteDoctorResponse['status'] | null>(null);
  const [invitesRefreshKey, setInvitesRefreshKey] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    const params = new URLSearchParams({ page: String(page) });
    if (filter) params.set('filter', filter);
    authFetch(`/operator/doctors?${params}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: OperatorDoctorListResponse) => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [authFetch, page, filter]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviteState('loading');
    setInviteResult(null);
    try {
      const res = await authFetch('/operator/doctors/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email:                inviteEmail,
          riziv_number:         inviteRiziv.trim() || undefined,
          riziv_qualification:  inviteQualification.trim() || undefined,
        }),
      });
      if (res.status === 409) { setInviteState('conflict'); return; }
      if (!res.ok) throw new Error();
      const result = await res.json() as InviteDoctorResponse;
      setInviteResult(result.status);
      setInviteState('success');
      setInviteEmail('');
      setInviteRiziv('');
      setInviteQualification('');
      load();
      setInvitesRefreshKey((k) => k + 1);
    } catch {
      setInviteState('error');
    }
  }

  return (
    <>
      <div className={panel.section}>
        <h2 className={panel.sectionHeading}>Arts uitnodigen</h2>
        <form onSubmit={handleInvite} className={panel.inlineForm}>
          <input
            type="email"
            required
            placeholder="E-mailadres arts"
            value={inviteEmail}
            onChange={(e) => { setInviteEmail(e.target.value); setInviteState('idle'); }}
            className={panel.field}
            style={{ minWidth: '220px' }}
          />
          <input
            type="text"
            placeholder="RIZIV-nummer (optioneel)"
            value={inviteRiziv}
            onChange={(e) => { setInviteRiziv(e.target.value); setInviteState('idle'); }}
            className={panel.field}
            style={{ width: '160px' }}
          />
          <input
            type="text"
            placeholder="Kwalificatie (optioneel)"
            value={inviteQualification}
            onChange={(e) => { setInviteQualification(e.target.value); setInviteState('idle'); }}
            className={panel.field}
            style={{ width: '180px' }}
          />
          <Button type="submit" variant="primary" disabled={inviteState === 'loading'}>
            {inviteState === 'loading' ? 'Bezig…' : 'Uitnodigen'}
          </Button>
          {inviteState === 'success' && inviteResult === 'profile_created' && (
            <span className={panel.successText}>Profiel aangemaakt — arts kan direct starten.</span>
          )}
          {inviteState === 'success' && inviteResult === 'invite_sent' && (
            <span className={panel.successText}>Uitnodiging verstuurd — arts wordt toegevoegd na registratie.</span>
          )}
          {inviteState === 'conflict' && (
            <span className={panel.warningText}>Dit e-mailadres is al geregistreerd als arts.</span>
          )}
          {inviteState === 'error' && (
            <span className={panel.errorText}>Uitnodiging mislukt. Probeer opnieuw.</span>
          )}
        </form>
      </div>

      <PendingInvitesPanel refreshKey={invitesRefreshKey} />

      <div className={panel.section}>
        <div className={panel.toolbar}>
          {([['', 'Alle'], ['pending', 'In verificatie'], ['locked', 'Vergrendeld'], ['verified', 'Geverifieerd']] as [FilterType, string][]).map(([val, label]) => (
            <button
              key={val}
              onClick={() => { setFilter(val); setPage(1); }}
              className={`${panel.pillFilter}${filter === val ? ` ${panel.pillFilterActive}` : ''}`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading && <p className="text-muted status-message">Laden…</p>}
        {error   && <p className="text-muted status-message">Kon artsenoverzicht niet laden.</p>}

        {data && !loading && (
          <>
            <div className={panel.resultCount}>{data.total} {data.total === 1 ? 'arts' : 'artsen'}</div>
            <div className={panel.tableWrap}>
              <table className={panel.table}>
                <thead>
                  <tr>
                    <Th>Naam</Th>
                    <Th>Email</Th>
                    <Th>RIZIV</Th>
                    <Th>Kwalificatie</Th>
                    <Th>Verificatie<br />status</Th>
                    <Th>Verificatie<br />Identiteit</Th>
                    <Th>Verificatie<br />Beroep</Th>
                    <Th>Verificatie<br />Overeenkomst</Th>
                    <Th>Verificatie<br />Pogingen</Th>
                    <Th>Aangemaakt op</Th>
                    <Th>Laatste login</Th>
                    <Th>Status</Th>
                    <Th>Acties</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.doctors.map((d) => <DoctorRow key={d.cognito_sub} doctor={d} onRizivSet={load} />)}
                  {data.doctors.length === 0 && (
                    <tr><td colSpan={13} className={panel.emptyCell}>Geen artsen gevonden.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <OperatorPagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>

      <PaymentsExportPanel />
    </>
  );
}

function PendingInvitesPanel({ refreshKey }: { refreshKey: number }) {
  const authFetch = useAuthFetch();
  const [data, setData]       = useState<PendingDoctorInviteListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [page, setPage]       = useState(1);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelErrorId, setCancelErrorId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    authFetch(`/operator/doctors/invites?page=${page}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: PendingDoctorInviteListResponse) => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [authFetch, page]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  async function handleCancel(invite: PendingDoctorInviteListResponse['invites'][number]) {
    if (!confirm(`Uitnodiging voor ${invite.email} intrekken?`)) return;
    setCancellingId(invite.id);
    setCancelErrorId(null);
    try {
      const res = await authFetch(`/operator/doctors/invites/${invite.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      load();
    } catch {
      setCancelErrorId(invite.id);
    } finally {
      setCancellingId(null);
    }
  }

  return (
    <div className={panel.section}>
      <h2 className={panel.sectionHeading}>Openstaande uitnodigingen{data ? ` (${data.total})` : ''}</h2>

      {loading && <p className="text-muted status-message">Laden…</p>}
      {error   && <p className="text-muted status-message">Kon uitnodigingen niet laden.</p>}

      {data && !loading && data.invites.length > 0 && (
        <>
          <div className={panel.tableWrap}>
            <table className={panel.table}>
              <thead>
                <tr>
                  <Th>Email</Th>
                  <Th>RIZIV</Th>
                  <Th>Kwalificatie</Th>
                  <Th>Uitgenodigd op</Th>
                  <Th>Acties</Th>
                </tr>
              </thead>
              <tbody>
                {data.invites.map((i) => (
                  <tr key={i.id}>
                    <td className={panel.td}>{i.email}</td>
                    <td className={panel.td} style={{ fontFamily: 'monospace' }}>{i.riziv_number ?? '—'}</td>
                    <td className={panel.td} style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {i.riziv_qualification ?? '—'}
                    </td>
                    <td className={panel.td} style={{ whiteSpace: 'nowrap' }}>{formatDateTimeFull(i.invited_at)}</td>
                    <td className={panel.td}>
                      <Button
                        variant="danger"
                        style={TABLE_BTN}
                        disabled={cancellingId === i.id}
                        onClick={() => handleCancel(i)}
                      >
                        {cancellingId === i.id ? '…' : 'Intrekken'}
                      </Button>
                      {cancelErrorId === i.id && <span className={panel.errorText} style={{ marginLeft: '0.5rem' }}>Mislukt</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <OperatorPagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      {data && !loading && data.invites.length === 0 && (
        <p className="text-muted status-message">Geen openstaande uitnodigingen.</p>
      )}
    </div>
  );
}

export function OperatorDoctors() {
  return (
    <OperatorPageShell maxWidth="1400px">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <Link to={ROUTES.operator} className={panel.mutedText}>← Beheerder</Link>
        <h1 className="page-heading" style={{ margin: 0 }}>Artsen</h1>
      </div>
      <OperatorDoctorsPanel />
    </OperatorPageShell>
  );
}

function PaymentsExportPanel() {
  const authFetch = useAuthFetch();
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [from, setFrom]   = useState('');
  const [to, setTo]       = useState('');

  async function handleExport() {
    setState('loading');
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to)   params.set('to', to);
      const res = await authFetch(`/operator/payments/export?${params}`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `betalingen-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setState('idle');
    } catch {
      setState('error');
    }
  }

  return (
    <div className={panel.section}>
      <h2 className={panel.sectionHeading}>Betalingen exporteren</h2>
      <p className="text-muted" style={{ marginTop: 0 }}>
        Exporteer betaalde transacties per arts als CSV (één rij per transactie), voor boekhouding.
      </p>
      <div className={panel.inlineForm}>
        <label className={panel.mutedText} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          Van
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={panel.field} />
        </label>
        <label className={panel.mutedText} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          Tot
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={panel.field} />
        </label>
        <Button variant="primary" onClick={handleExport} disabled={state === 'loading'}>
          {state === 'loading' ? 'Bezig…' : 'Exporteer CSV'}
        </Button>
        {state === 'error' && <span className={panel.errorText}>Export mislukt. Probeer opnieuw.</span>}
      </div>
    </div>
  );
}

const STATUS_LABEL: Record<ReturnType<typeof doctorStatus>, string> = {
  locked:   'Vergrendeld',
  verified: 'Geverifieerd',
  pending:  'In verificatie',
};

const STATUS_TONE: Record<ReturnType<typeof doctorStatus>, PillTone> = {
  locked:   'warning',
  verified: 'success',
  pending:  'neutral',
};

function DoctorRow({ doctor: d, onRizivSet }: { doctor: OperatorDoctorSummary; onRizivSet: () => void }) {
  const authFetch = useAuthFetch();
  const authUser   = useAuthUser();
  const isSelf     = d.cognito_sub === authUser?.userId;
  const status = doctorStatus(d);

  const [editing, setEditing]         = useState(false);
  const [rizivNumber, setRizivNumber] = useState('');
  const [qualification, setQualification] = useState('');
  const [saving, setSaving]           = useState(false);
  const [saveError, setSaveError]     = useState(false);

  const [tosSaving, setTosSaving] = useState(false);
  const [tosError, setTosError]   = useState(false);

  const suspended = d.suspended_at !== null;
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendEditing, setSuspendEditing] = useState(false);
  const [accountSaving, setAccountSaving]   = useState(false);
  const [accountError, setAccountError]     = useState(false);

  async function handleSaveRiziv(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveError(false);
    try {
      const res = await authFetch(`/operator/doctors/${d.cognito_sub}/riziv`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          riziv_number:        rizivNumber.trim(),
          riziv_qualification: qualification.trim() || undefined,
        }),
      });
      if (!res.ok) throw new Error();
      setEditing(false);
      onRizivSet();
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  }

  async function handleAcceptTos() {
    if (!confirm(`Overeenkomst accepteren namens ${d.first_name} ${d.last_name}? Gebruik dit alleen als de arts hier zelf niet bij kan (bv. de RIZIV-zelfbedieningsflow ligt eruit).`)) return;
    setTosSaving(true);
    setTosError(false);
    try {
      const res = await authFetch(`/operator/doctors/${d.cognito_sub}/tos-accept`, { method: 'PATCH' });
      if (!res.ok) throw new Error();
      onRizivSet();
    } catch {
      setTosError(true);
    } finally {
      setTosSaving(false);
    }
  }

  async function handleDeactivate(e: React.FormEvent) {
    e.preventDefault();
    setAccountSaving(true);
    setAccountError(false);
    try {
      const res = await authFetch(`/operator/users/${d.cognito_sub}/suspend`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ reason: suspendReason.trim() || undefined }),
      });
      if (!res.ok) throw new Error();
      setSuspendEditing(false);
      setSuspendReason('');
      onRizivSet();
    } catch {
      setAccountError(true);
    } finally {
      setAccountSaving(false);
    }
  }

  async function handleReactivate() {
    if (!confirm(`Account van ${d.first_name} ${d.last_name} heractiveren?`)) return;
    setAccountSaving(true);
    setAccountError(false);
    try {
      const res = await authFetch(`/operator/users/${d.cognito_sub}/unsuspend`, { method: 'POST' });
      if (!res.ok) throw new Error();
      onRizivSet();
    } catch {
      setAccountError(true);
    } finally {
      setAccountSaving(false);
    }
  }

  return (
    <tr>
      <td className={panel.td}>{d.first_name} {d.last_name}</td>
      <td className={panel.td}>{d.email ?? '—'}</td>
      <td className={panel.td} style={{ fontFamily: 'monospace' }}>{d.riziv_number ?? '—'}</td>
      <td className={panel.td} style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {d.riziv_qualification ?? '—'}
      </td>
      <td className={panel.td}>
        <Pill tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Pill>
      </td>
      <td className={panel.td}><StatusDot ok={d.identity_verified} /></td>
      <td className={panel.td}>
        {d.profession_verified ? (
          <StatusDot ok />
        ) : editing ? (
          <form onSubmit={handleSaveRiziv} className={panel.inlineForm}>
            <input
              type="text"
              required
              autoFocus
              placeholder="RIZIV-nummer"
              value={rizivNumber}
              onChange={(e) => setRizivNumber(e.target.value)}
              className={`${panel.field} ${panel.fieldSm}`}
              style={{ width: '110px' }}
            />
            <input
              type="text"
              placeholder="Kwalificatie"
              value={qualification}
              onChange={(e) => setQualification(e.target.value)}
              className={`${panel.field} ${panel.fieldSm}`}
              style={{ width: '140px' }}
            />
            <Button type="submit" variant="primary" style={TABLE_BTN} disabled={saving}>{saving ? '…' : 'Bewaar'}</Button>
            <Button type="button" variant="ghost" style={TABLE_BTN} onClick={() => setEditing(false)}>Annuleer</Button>
            {saveError && <span className={panel.errorText}>Mislukt</span>}
          </form>
        ) : (
          <button onClick={() => setEditing(true)} className={panel.linkButton}>RIZIV instellen</button>
        )}
      </td>
      <td className={panel.td}>
        {d.tos_accepted ? (
          <StatusDot ok />
        ) : d.profession_verified ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <button onClick={handleAcceptTos} disabled={tosSaving} className={panel.linkButton}>
              {tosSaving ? '…' : 'Namens arts accepteren'}
            </button>
            {tosError && <span className={panel.errorText}>Mislukt</span>}
          </div>
        ) : (
          <StatusDot ok={false} />
        )}
      </td>
      <td className={panel.td} style={status === 'locked' ? { color: 'var(--c-warning-text)' } : undefined}>
        {d.verification_attempts}
      </td>
      <td className={panel.td} style={{ whiteSpace: 'nowrap' }}>{formatDateTimeFull(d.created_at)}</td>
      <td className={panel.td} style={{ whiteSpace: 'nowrap' }}>{formatDateTimeFull(d.last_login_at)}</td>
      <td className={panel.td}>
        <AccountStatusPill suspended={suspended} reason={d.suspended_reason} />
      </td>
      <td className={panel.td}>
        {isSelf ? (
          <span className={panel.mutedText}>Dit ben jij</span>
        ) : suspendEditing ? (
          <form onSubmit={handleDeactivate} className={panel.inlineForm}>
            <input
              type="text"
              autoFocus
              placeholder="Reden (optioneel)"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              className={`${panel.field} ${panel.fieldSm}`}
              style={{ width: '160px' }}
            />
            <Button type="submit" variant="danger" style={TABLE_BTN} disabled={accountSaving}>{accountSaving ? '…' : 'Deactiveren'}</Button>
            <Button type="button" variant="ghost" style={TABLE_BTN} onClick={() => setSuspendEditing(false)}>Annuleer</Button>
          </form>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {suspended ? (
              <Button variant="ghost" style={TABLE_BTN} onClick={handleReactivate} disabled={accountSaving}>
                {accountSaving ? '…' : 'Heractiveren'}
              </Button>
            ) : (
              <Button variant="danger" style={TABLE_BTN} onClick={() => setSuspendEditing(true)} disabled={accountSaving}>
                Deactiveren
              </Button>
            )}
          </div>
        )}
        {accountError && <span className={panel.errorText} style={{ marginLeft: '0.5rem' }}>Mislukt</span>}
      </td>
    </tr>
  );
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span style={{ color: ok ? 'var(--c-success-text)' : 'var(--c-text-light)' }}>
      {ok ? '✓' : '—'}
    </span>
  );
}
